import React, { useState, useEffect } from 'react';
import { DomainResult, HearingResponses } from '../../types/index';

interface HearingAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
}

type HearingStep = 
  | 'intro' 
  | 'filter'
  | 'assessment_mode_choice'
  | 'self_assessment'
  | 'assisted_preparation'
  | 'assisted_whisper_left'
  | 'assisted_whisper_right'
  | 'results' 
  | 'carer_correction'
  | 'completed';

type AssessmentMode = 'self_assessment' | 'assisted' | 'skip';

const HearingAssessment: React.FC<HearingAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<HearingStep>('intro');
  const [responses, setResponses] = useState<Partial<HearingResponses>>({});
  const [assessmentMode, setAssessmentMode] = useState<AssessmentMode>('self_assessment');
  // const hasCompletedRef = useRef<boolean>(false); // Removed: not used in current implementation
  
  // 照护者修正结果
  const [carerCorrection, setCarerCorrection] = useState({
    selfAssessmentCorrect: null as boolean | null,
    leftEarCorrect: null as boolean | null,
    rightEarCorrect: null as boolean | null
  });

  // 评估逻辑实现
  const evaluateHearing = (): DomainResult => {
    // Filter Question优先级最高
    if (responses.filterQuestion === true) {
      return {
        status: 'FAIL',
        completionMethod: 'filter_question',
        filterQuestionResult: false, // 标记filter question未通过
        testResults: [
          {
            name: "听力问题筛查",
            passed: false,
            details: "筛查问题：存在听力问题",
            rawUserAnswers: {
              filterQuestion: true
            }
          }
        ],
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C9-S9.2-Community-Care-Hearing"
        ],
        // 🎯 双数据源支持：筛查问题失败时也添加responses字段
        responses: {
          filterQuestion: true
        }
      };
    }
    
    // 如果选择跳过测试
    if (assessmentMode === 'skip') {
      return {
        status: 'INCONCLUSIVE',
        completionMethod: 'test_skipped',
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C9-S9.2-Community-Care-Hearing"
        ]
      };
    }
    
    // 自我评估模式
    if (assessmentMode === 'self_assessment') {
      const selfAssessmentPassed = responses.selfAssessment?.overallHearing === 'good';
      return {
        status: selfAssessmentPassed ? 'PASS' : 'FAIL',
        completionMethod: 'independent_test',
        filterQuestionResult: true, // 🎯 修复缺失：自我评估说明筛查通过
        testResults: [
          { 
            name: "听力自我评估", 
            passed: selfAssessmentPassed,
            rawUserAnswers: {
              filterQuestion: responses.filterQuestion,
              overallHearing: responses.selfAssessment?.overallHearing,
              overallHearingPassed: selfAssessmentPassed
            }
          }
        ],
        recommendations: selfAssessmentPassed ? 
          ["ICOPE-C3-S3.1.3-Advice-EarHealth"] : 
          ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C9-S9.2-Community-Care-Hearing"]
      };
    }
    
    // 辅助测试模式
    const leftEarPassed = responses.whisperTest?.leftEar === true;
    const rightEarPassed = responses.whisperTest?.rightEar === true;
    const bothEarsPassed = leftEarPassed && rightEarPassed;
    
    const testResults = [
      { 
        name: "左耳耳语测试", 
        passed: leftEarPassed,
        rawUserAnswers: {
          filterQuestion: responses.filterQuestion,
          leftEarHeard: responses.whisperTest?.leftEar,
          rightEarHeard: responses.whisperTest?.rightEar,
          leftEarPassed: leftEarPassed,
          rightEarPassed: rightEarPassed
        }
      },
      { 
        name: "右耳耳语测试", 
        passed: rightEarPassed,
        rawUserAnswers: {
          filterQuestion: responses.filterQuestion,
          leftEarHeard: responses.whisperTest?.leftEar,
          rightEarHeard: responses.whisperTest?.rightEar,
          leftEarPassed: leftEarPassed,
          rightEarPassed: rightEarPassed
        }
      }
    ];

    return {
      status: bothEarsPassed ? 'PASS' : 'FAIL',
      completionMethod: 'assisted_test',
      filterQuestionResult: true, // 🎯 修复关键缺失：辅助测试说明筛查通过
      testResults,
      recommendations: bothEarsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-EarHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C9-S9.2-Community-Care-Hearing"]
    };
  };

  const handleFilterResponse = (hasProblems: boolean) => {
    const updatedResponses = { ...responses, filterQuestion: hasProblems };
    setResponses(updatedResponses);
    
    if (hasProblems) {
      // 筛查问题失败，直接生成结果
      const result = {
        status: 'FAIL' as const,
        completionMethod: 'filter_question' as const,
        filterQuestionResult: false, // 标记filter question未通过
        testResults: [{
          name: "听力问题筛查",
          passed: false,
          details: "筛查问题：存在听力问题",
          rawUserAnswers: {
            filterQuestion: true
          }
        }],
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C9-S9.2-Community-Care-Hearing"
        ],
        // 🎯 双数据源支持：筛查问题失败时也添加responses字段
        responses: {
          filterQuestion: true
        }
      };
      console.log('📋 听力筛查问题失败，完成评估:', result);
      onComplete(result);
      setCurrentStep('completed');
    } else {
      setCurrentStep('assessment_mode_choice');
    }
  };

  const handleModeChoice = (mode: AssessmentMode) => {
    setAssessmentMode(mode);
    setResponses(prev => ({ ...prev, assessmentMode: mode }));
    
    if (mode === 'skip') {
      // 跳过测试，直接生成结果
      const result = {
        status: 'INCONCLUSIVE' as const,
        completionMethod: 'test_skipped' as const,
        filterQuestionResult: true, // 筛查通过但跳过了测试
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C9-S9.2-Community-Care-Hearing"
        ]
      };
      console.log('📋 听力测试跳过，完成评估:', result);
      onComplete(result);
      setCurrentStep('completed');
    } else if (mode === 'self_assessment') {
      setCurrentStep('self_assessment');
    } else {
      setCurrentStep('assisted_preparation');
    }
  };

  const handleSelfAssessment = (result: { overallHearing: 'good' | 'poor'; difficulties?: string[] }) => {
    setResponses(prev => ({ ...prev, selfAssessment: result }));
    setCurrentStep('results');
  };

  const handleWhisperTest = (ear: 'left' | 'right', canHear: boolean) => {
    const updatedResponses = {
      ...responses,
      whisperTest: {
        ...responses.whisperTest,
        [ear === 'left' ? 'leftEar' : 'rightEar']: canHear
      }
    };
    setResponses(updatedResponses);

    if (ear === 'left') {
      setCurrentStep('assisted_whisper_right');
    } else {
      setCurrentStep('results');
    }
  };

  // 处理测试结果确认
  const handleResultsConfirm = () => {
    const result = evaluateHearing();
    onComplete(result);
    setCurrentStep('completed');
  };

  // 处理照护者修正
  const handleCarerCorrection = (selfAssess: boolean | null, leftEar: boolean | null, rightEar: boolean | null) => {
    setCarerCorrection({ 
      selfAssessmentCorrect: selfAssess, 
      leftEarCorrect: leftEar, 
      rightEarCorrect: rightEar 
    });
    
    let testResults = [];
    let allTestsPassed = false;
    
    if (assessmentMode === 'self_assessment') {
      allTestsPassed = selfAssess === true;
      testResults = [
        { name: "听力自我评估", passed: selfAssess === true, details: "照护者确认" }
      ];
    } else {
      allTestsPassed = leftEar === true && rightEar === true;
      testResults = [
        { name: "左耳耳语测试", passed: leftEar === true, details: "照护者确认" },
        { name: "右耳耳语测试", passed: rightEar === true, details: "照护者确认" }
      ];
    }
    
    const result: DomainResult = {
      status: allTestsPassed ? 'PASS' : 'FAIL',
      completionMethod: 'carer_corrected',
      testResults,
      recommendations: allTestsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-EarHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C9-S9.2-Community-Care-Hearing"]
    };
    
    onComplete(result);
    setCurrentStep('completed');
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => onNext(), 1500);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const renderIntro = () => (
    <div className="text-center space-y-6 p-6">
      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 12.414a9 9 0 010-8.828m-2.879 11.657a5 5 0 010-7.071" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">听力健康评估</h2>
      <p className="text-gray-600 max-w-md mx-auto">
        通过简单的问题和测试评估您的听力状况。良好的听力对于日常交流和生活质量非常重要。
      </p>
      <button 
        onClick={() => setCurrentStep('filter')} 
        className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
      >
        开始评估
      </button>
    </div>
  );

  const renderFilter = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">听力问题筛查</h2>
        <p className="text-gray-600">请根据您的实际情况诚实回答</p>
      </div>
      
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <p className="font-semibold text-lg mb-4 text-gray-900">
          您是否有听力方面的困难？
        </p>
        <p className="text-sm text-gray-600 mb-4">
          （对于使用助听器的用户，请考虑即使佩戴助听器时是否仍有困难）
        </p>
        
        <div className="bg-white rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-3">常见听力困难包括：</h4>
          <div className="space-y-2 text-gray-700">
            <div className="flex items-start space-x-2">
              <span className="text-indigo-500 mt-1">•</span>
              <span>在嘈杂环境中听不清对话</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-indigo-500 mt-1">•</span>
              <span>经常需要让别人重复说话</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-indigo-500 mt-1">•</span>
              <span>需要调高电视或收音机音量</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-indigo-500 mt-1">•</span>
              <span>听不到门铃或电话铃声</span>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button 
            onClick={() => handleFilterResponse(true)} 
            className="w-full p-4 bg-red-100 border-2 border-red-200 rounded-lg text-left hover:bg-red-200 transition-colors"
          >
            <span className="font-medium text-red-800">是的，我有上述听力困难</span>
          </button>
          <button 
            onClick={() => handleFilterResponse(false)} 
            className="w-full p-4 bg-green-100 border-2 border-green-200 rounded-lg text-left hover:bg-green-200 transition-colors"
          >
            <span className="font-medium text-green-800">没有，我听力正常</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderAssessmentModeChoice = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">选择评估方式</h2>
        <p className="text-gray-600">请选择最适合您的听力评估方式</p>
      </div>
      
      <div className="space-y-4">
        <button
          onClick={() => handleModeChoice('assisted')}
          className="w-full p-6 bg-green-50 border-2 border-green-200 rounded-lg text-left hover:bg-green-100 transition-colors"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-900 mb-2">耳语测试（准确度高，需照护者协助）</h3>
              <p className="text-sm text-green-700">家人或照护者将协助您完成专业的耳语测试</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => handleModeChoice('self_assessment')}
          className="w-full p-6 bg-blue-50 border-2 border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">问答测试（准确度低，可自行完成）</h3>
              <p className="text-sm text-blue-700">通过回答关于日常听力体验的问题来评估</p>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => handleModeChoice('skip')}
          className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg text-center hover:bg-gray-100 transition-colors"
        >
          <span className="text-gray-600">暂时跳过听力测试</span>
        </button>
      </div>
    </div>
  );

  const renderWhisperTest = (ear: 'left' | 'right') => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
          ear === 'left' ? 'bg-blue-100' : 'bg-green-100'
        }`}>
          <svg className={`w-8 h-8 ${ear === 'left' ? 'text-blue-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 12.414a9 9 0 010-8.828m-2.879 11.657a5 5 0 010-7.071" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {ear === 'left' ? '左耳' : '右耳'}耳语测试
        </h2>
        <p className="text-gray-600">请协助者按照步骤进行测试</p>
      </div>

      {/* 演示视频区域 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          🎥 操作演示视频
        </h3>
        <div className="relative">
          <video 
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-auto rounded-lg shadow-sm"
            style={{ maxHeight: '300px' }}
            onError={(e) => {
              // 视频加载失败时，隐藏视频区域并显示备用提示
              console.error('Auto-play video loading error:', e);
              console.error('Auto-play video src:', e.currentTarget.src);
              const target = e.currentTarget as HTMLVideoElement;
              target.style.display = 'none';
              const nextElement = target.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'block';
              }
            }}
            onLoadStart={() => console.log('Auto-play video loading started')}
            onCanPlay={() => console.log('Auto-play video can play')}
            onLoadedData={() => console.log('Auto-play video loaded successfully')}
          >
            <source src="/images/assessment/whisper_voice_test.mp4?v=compressed" type="video/mp4" />
            <source src="./images/assessment/whisper_voice_test.mp4" type="video/mp4" />
            您的浏览器不支持视频播放。
          </video>
          
          {/* 视频加载失败时的备用显示 */}
          <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hidden">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H8.25a.75.75 0 01-.75-.75v-6a.75.75 0 01.75-.75h7.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M25.5 10.5v15" />
            </svg>
            <p className="text-sm text-gray-600 font-medium mb-2">耳语测试演示视频</p>
            <p className="text-xs text-gray-500 mb-3">
              演示视频正在加载中，请稍候...
            </p>
            <div className="text-xs text-gray-400 space-y-1">
              <p>演示内容：正确的耳语测试姿势和操作方法</p>
              <p>包括：距离控制、耳屏按压、声音大小示范</p>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
          <p className="text-sm text-indigo-800">
            📹 <strong>观看要点：</strong> 请仔细观看视频中的距离控制、手势操作和耳语音量，确保测试的准确性
          </p>
        </div>
      </div>

      <div className={`border rounded-lg p-6 ${
        ear === 'left' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
      }`}>
        <h3 className="font-semibold text-gray-900 mb-4">🎯 测试步骤：</h3>
        <div className="space-y-4 text-gray-700 mb-6">
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-gray-600">1</span>
            <p>请用户用手指轻轻按压{ear === 'left' ? '右耳耳屏' : '左耳耳屏'}，堵住{ear === 'left' ? '右耳' : '左耳'}</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-gray-600">2</span>
            <p>协助者站在用户{ear === 'left' ? '左后侧' : '右后侧'}约60厘米处</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-gray-600">3</span>
            <p>用<strong>耳语声音</strong>（不是正常说话声）清晰说出测试词语</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-gray-600">4</span>
            <p>观察用户是否能正确重复听到的词语</p>
          </div>
        </div>

        {/* 左耳测试时显示切换提示 */}
        {ear === 'left' && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 text-center">
              记录左耳结果后，将自动开始右耳测试
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-2">💬 建议测试词语：</h4>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
            <span>"九十四"</span>
            <span>"你好"</span>
            <span>"苹果"</span>
            <span>"星期三"</span>
          </div>
          <p className="text-xs text-gray-600 mt-2">请选择其中2-3个词语进行测试</p>
        </div>

        {/* 左右耳进度指示 */}
        <div className="flex items-center justify-center space-x-3 mb-4">
          <span className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            ear === 'left'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-200 text-gray-500'
          }`}>
            1. 左耳
          </span>
          <span className="text-gray-400 text-lg">→</span>
          <span className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            ear === 'right'
              ? 'bg-green-500 text-white shadow-md'
              : 'bg-gray-200 text-gray-500'
          }`}>
            2. 右耳
          </span>
        </div>

        <div className="space-y-3">
          <p className="font-medium text-gray-900 mb-3">测试结果记录：</p>
          <button
            onClick={() => handleWhisperTest(ear, true)}
            className="w-full p-4 bg-green-100 border-2 border-green-200 rounded-lg text-left hover:bg-green-200 transition-colors"
          >
            <span className="font-medium text-green-800">用户能正确重复大部分词语</span>
            <p className="text-sm text-green-700 mt-1">（至少能重复3个词语中的2个或以上）</p>
          </button>
          <button
            onClick={() => handleWhisperTest(ear, false)}
            className="w-full p-4 bg-red-100 border-2 border-red-200 rounded-lg text-left hover:bg-red-200 transition-colors"
          >
            <span className="font-medium text-red-800">用户重复有困难</span>
            <p className="text-sm text-red-700 mt-1">（重复错误或只能部分听清）</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    // 根据评估模式显示不同的结果
    if (assessmentMode === 'self_assessment') {
      const selfAssessmentPassed = responses.selfAssessment?.overallHearing === 'good';
      
      return (
        <div className="p-6 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">听力测试结果</h2>
            <p className="text-gray-600">系统自动评估结果</p>
          </div>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${selfAssessmentPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <h3 className="font-semibold text-gray-900 mb-2">听力自我评估</h3>
              <p className="text-sm">
                评估结果：{selfAssessmentPassed ? '听力状况良好' : '听力有困难'} {selfAssessmentPassed ? '✅' : '❌'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              确认结果，完成测试
            </button>
            
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              照护者修正答案
            </button>
          </div>
        </div>
      );
    } else {
      // 辅助测试模式
      const leftEarPassed = responses.whisperTest?.leftEar === true;
      const rightEarPassed = responses.whisperTest?.rightEar === true;
      
      return (
        <div className="p-6 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">听力测试结果</h2>
            <p className="text-gray-600">耳语测试评估结果</p>
          </div>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${leftEarPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <h3 className="font-semibold text-gray-900 mb-2">左耳耳语测试</h3>
              <p className="text-sm">
                结果：{leftEarPassed ? '能正确重复词语' : '重复有困难'} {leftEarPassed ? '✅' : '❌'}
              </p>
            </div>

            <div className={`p-4 rounded-lg border ${rightEarPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <h3 className="font-semibold text-gray-900 mb-2">右耳耳语测试</h3>
              <p className="text-sm">
                结果：{rightEarPassed ? '能正确重复词语' : '重复有困难'} {rightEarPassed ? '✅' : '❌'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              确认结果，完成测试
            </button>
            
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              照护者修正答案
            </button>
          </div>
        </div>
      );
    }
  };

  // 照护者修正页面
  const renderCarerCorrection = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
        <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
      </div>

      <div className="space-y-6">
        {assessmentMode === 'self_assessment' ? (
          // 自我评估模式的修正
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">听力自我评估结果确认</h3>
            <div className="text-sm text-gray-600 mb-4">
              用户评估：{responses.selfAssessment?.overallHearing === 'good' ? '听力状况良好' : '听力有困难'}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, selfAssessmentCorrect: true }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.selfAssessmentCorrect === true 
                    ? 'bg-green-100 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 hover:border-green-400'
                }`}
              >
                ✅ 听力良好
              </button>
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, selfAssessmentCorrect: false }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.selfAssessmentCorrect === false 
                    ? 'bg-red-100 border-red-500 text-red-700' 
                    : 'bg-white border-gray-300 hover:border-red-400'
                }`}
              >
                ❌ 有听力困难
              </button>
            </div>
          </div>
        ) : (
          // 辅助测试模式的修正
          <>
            {/* 左耳确认 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">左耳测试结果确认</h3>
              <div className="text-sm text-gray-600 mb-4">
                测试表现：{responses.whisperTest?.leftEar ? '能正确重复词语' : '重复有困难'}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, leftEarCorrect: true }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                    carerCorrection.leftEarCorrect === true 
                      ? 'bg-green-100 border-green-500 text-green-700' 
                      : 'bg-white border-gray-300 hover:border-green-400'
                  }`}
                >
                  ✅ 左耳听力正常
                </button>
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, leftEarCorrect: false }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                    carerCorrection.leftEarCorrect === false 
                      ? 'bg-red-100 border-red-500 text-red-700' 
                      : 'bg-white border-gray-300 hover:border-red-400'
                  }`}
                >
                  ❌ 左耳有困难
                </button>
              </div>
            </div>

            {/* 右耳确认 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">右耳测试结果确认</h3>
              <div className="text-sm text-gray-600 mb-4">
                测试表现：{responses.whisperTest?.rightEar ? '能正确重复词语' : '重复有困难'}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, rightEarCorrect: true }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                    carerCorrection.rightEarCorrect === true 
                      ? 'bg-green-100 border-green-500 text-green-700' 
                      : 'bg-white border-gray-300 hover:border-green-400'
                  }`}
                >
                  ✅ 右耳听力正常
                </button>
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, rightEarCorrect: false }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                    carerCorrection.rightEarCorrect === false 
                      ? 'bg-red-100 border-red-500 text-red-700' 
                      : 'bg-white border-gray-300 hover:border-red-400'
                  }`}
                >
                  ❌ 右耳有困难
                </button>
              </div>
            </div>
          </>
        )}

        <button
          onClick={() => {
            if (assessmentMode === 'self_assessment') {
              if (carerCorrection.selfAssessmentCorrect !== null) {
                handleCarerCorrection(
                  carerCorrection.selfAssessmentCorrect,
                  null,
                  null
                );
              }
            } else {
              if (carerCorrection.leftEarCorrect !== null && carerCorrection.rightEarCorrect !== null) {
                handleCarerCorrection(
                  null,
                  carerCorrection.leftEarCorrect,
                  carerCorrection.rightEarCorrect
                );
              }
            }
          }}
          disabled={
            assessmentMode === 'self_assessment' 
              ? carerCorrection.selfAssessmentCorrect === null
              : (carerCorrection.leftEarCorrect === null || carerCorrection.rightEarCorrect === null)
          }
          className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          确认修正，完成评估
        </button>
      </div>
    </div>
  );

  const renderCompleted = () => (
    <div className="text-center p-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">听力评估完成</h2>
      <p className="text-gray-600">正在转到下一项...</p>
    </div>
  );

  const renderSelfAssessment = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">听力自我评估</h2>
        <p className="text-gray-600">请根据您的日常体验回答以下问题</p>
      </div>
      
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">总体听力状况评估</h3>
        
        <div className="space-y-4 mb-6">
          <div className="bg-white rounded-lg p-4">
            <p className="font-medium text-gray-900 mb-3">在以下情况下，您的听力表现如何？</p>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <span className="text-indigo-500">•</span>
                <span>与家人面对面交谈时</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-indigo-500">•</span>
                <span>在相对安静的环境中对话</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-indigo-500">•</span>
                <span>听电视、广播的声音</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-indigo-500">•</span>
                <span>接听电话时</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => handleSelfAssessment({ 
              overallHearing: 'good',
              difficulties: []
            })}
            className="w-full p-4 bg-green-100 border-2 border-green-200 rounded-lg text-left hover:bg-green-200 transition-colors"
          >
            <div>
              <span className="font-medium text-green-800">听力状况良好</span>
              <p className="text-sm text-green-700 mt-1">上述情况下都能正常听清，无明显困难</p>
            </div>
          </button>
          
          <button
            onClick={() => handleSelfAssessment({ 
              overallHearing: 'poor',
              difficulties: ['conversation', 'tv_radio', 'phone']
            })}
            className="w-full p-4 bg-red-100 border-2 border-red-200 rounded-lg text-left hover:bg-red-200 transition-colors"
          >
            <div>
              <span className="font-medium text-red-800">听力有困难</span>
              <p className="text-sm text-red-700 mt-1">在上述一种或多种情况下有听力困难</p>
            </div>
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-800">
            💡 <strong>提示：</strong> 如果您不确定，建议选择"听力有困难"以获得更详细的建议
          </p>
        </div>
      </div>
    </div>
  );
  
  const renderAssistedPreparation = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">辅助测试准备</h2>
        <p className="text-gray-600">请家人或照护者协助完成耳语测试</p>
      </div>

      {/* 演示视频区域 - 准备阶段 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          🎥 耳语测试操作演示
        </h3>
        <div className="relative">
          <video 
            controls
            muted
            playsInline
            preload="metadata"
            className="w-full h-auto rounded-lg shadow-sm"
            style={{ maxHeight: '280px' }}
            onError={(e) => {
              console.error('Video loading error:', e);
              console.error('Video src:', e.currentTarget.src);
              console.error('Current location:', window.location.href);
              const target = e.currentTarget as HTMLVideoElement;
              target.style.display = 'none';
              const nextElement = target.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'block';
              }
            }}
            onLoadStart={() => console.log('Video loading started')}
            onCanPlay={() => console.log('Video can play')}
            onLoadedData={() => console.log('Video loaded successfully')}
            onAbort={() => console.log('Video loading aborted')}
            onStalled={() => console.log('Video loading stalled')}
          >
            <source src="/images/assessment/whisper_voice_test.mp4?v=compressed" type="video/mp4" />
            <source src="./images/assessment/whisper_voice_test.mp4" type="video/mp4" />
            您的浏览器不支持视频播放。
          </video>
          
          {/* 视频加载失败时的备用显示 */}
          <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hidden">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H8.25a.75.75 0 01-.75-.75v-6a.75.75 0 01.75-.75h7.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M25.5 10.5v15" />
            </svg>
            <p className="text-sm text-gray-600 font-medium mb-2">耳语测试操作演示</p>
            <p className="text-xs text-gray-500 mb-3">
              演示视频正在加载中，请稍候...
            </p>
            <div className="text-xs text-gray-400 space-y-1">
              <p>演示内容：正确的耳语测试姿势和操作方法</p>
              <p>包括：距离控制、耳屏按压、声音大小示范</p>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm text-green-800">
            📺 <strong>建议：</strong> 请协助者先观看完整演示视频，了解正确的测试方法后再开始实际测试
          </p>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">📋 给协助者的说明：</h3>
        <div className="space-y-4 text-gray-700">
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-green-600">1</span>
            <p><strong>环境准备：</strong> 找一个相对安静的房间进行测试</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-green-600">2</span>
            <p><strong>测试方法：</strong> 将分别测试左耳和右耳，每次测试时用手指轻压对侧耳朵</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-green-600">3</span>
            <p><strong>测试距离：</strong> 站在用户身后约60厘米（一臂距离）处</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-green-600">4</span>
            <p><strong>测试声音：</strong> 用耳语（不是正常说话声）说出简单词语</p>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            ⚠️ <strong>重要提醒：</strong> 请确保环境相对安静，避免干扰测试结果
          </p>
        </div>
      </div>
      
      <button
        onClick={() => setCurrentStep('assisted_whisper_left')}
        className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
      >
        开始辅助测试
      </button>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'intro': return renderIntro();
      case 'filter': return renderFilter();
      case 'assessment_mode_choice': return renderAssessmentModeChoice();
      case 'self_assessment': return renderSelfAssessment();
      case 'assisted_preparation': return renderAssistedPreparation();
      case 'assisted_whisper_left': return renderWhisperTest('left');
      case 'assisted_whisper_right': return renderWhisperTest('right');
      case 'results': return renderResults();
      case 'carer_correction': return renderCarerCorrection();
      case 'completed': return renderCompleted();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default HearingAssessment;